import { parse } from '@babel/parser';

/** Só o que a gente usa de um nó do babel — evita arrastar @babel/types. */
interface Node {
	type: string;
	start?: number | null;
	end?: number | null;
	[key: string]: unknown;
}

export interface JsxAttr {
	name: string;
	start: number;
	end: number;
	/** conteúdo quando o valor é string literal; null em {expressão} ou atributo solto */
	value: string | null;
	/** miolo da string, sem as aspas (-1 quando value é null) */
	valueStart: number;
	valueEnd: number;
}

export interface JsxElement {
	/** "início-fim" da tag de abertura: é o que viaja até o DOM em data-cb-loc */
	loc: string;
	name: string;
	start: number;
	end: number;
	/** logo depois do nome da tag — onde entra atributo novo */
	nameEnd: number;
	/** fim do elemento INTEIRO, incluindo filhos e tag de fechamento */
	outerEnd: number;
	attrs: JsxAttr[];
	/** primeiro filho de texto não vazio */
	text: { start: number; end: number } | null;
	/** vão entre <a> e </a>, para quando o elemento está vazio */
	inner: { start: number; end: number } | null;
	/** único filho em {expressão}: <span>{s.titulo}</span> */
	expr: { start: number; end: number } | null;
	/** o conteúdo é só texto (ou só uma expressão): não embrulha outros elementos */
	folhaDeTexto: boolean;
}

export type Papel = 'raiz' | 'botao' | 'imagem' | 'texto' | 'superficie';

export function parseJsx(src: string): unknown {
	return parse(src, { sourceType: 'module', plugins: ['jsx', 'typescript'], errorRecovery: false });
}

/** Visita todo nó com `type`. 15 linhas em vez de @babel/traverse. */
export function walk(node: unknown, fn: (n: Node) => void): void {
	if (!node || typeof node !== 'object') return;
	if (Array.isArray(node)) {
		for (const item of node) walk(item, fn);
		return;
	}
	const n = node as Node;
	if (typeof n.type === 'string') fn(n);
	for (const key in n) {
		if (key === 'loc' || key === 'leadingComments' || key === 'trailingComments') continue;
		walk(n[key], fn);
	}
}

function at(n: Node, key: 'start' | 'end'): number {
	return typeof n[key] === 'number' ? (n[key] as number) : 0;
}

function jsxName(n: Node): string {
	if (n.type === 'JSXIdentifier') return String(n.name);
	if (n.type === 'JSXMemberExpression') return `${jsxName(n.object as Node)}.${jsxName(n.property as Node)}`;
	if (n.type === 'JSXNamespacedName') return `${jsxName(n.namespace as Node)}:${jsxName(n.name as Node)}`;
	return '?';
}

function toAttr(a: Node): JsxAttr {
	const value = a.value as Node | null;
	const isString = !!value && value.type === 'StringLiteral';
	return {
		name: jsxName(a.name as Node),
		start: at(a, 'start'),
		end: at(a, 'end'),
		value: isString ? String(value.value) : null,
		valueStart: isString ? at(value, 'start') + 1 : -1,
		valueEnd: isString ? at(value, 'end') - 1 : -1,
	};
}

/** Filho que conta: espaço em branco entre tags não é conteúdo. */
function filhosUteis(children: Node[]): Node[] {
	return children.filter((c) => c.type !== 'JSXText' || String(c.value).trim() !== '');
}

function ehExpressaoDeTexto(expr: Node): boolean {
	let temJsx = false;
	walk(expr, (n) => {
		if (n.type === 'JSXElement' || n.type === 'JSXFragment') temJsx = true;
	});
	return !temJsx;
}

function describe(el: Node): JsxElement {
	const open = el.openingElement as Node;
	const close = (el.closingElement ?? null) as Node | null;
	const children = (el.children ?? []) as Node[];
	const text = children.find((c) => c.type === 'JSXText' && String(c.value).trim() !== '') ?? null;
	const uteis = filhosUteis(children);
	const so = uteis.length === 1 && uteis[0].type === 'JSXExpressionContainer' ? (uteis[0].expression as Node) : null;
	const expr = so && so.type !== 'JSXEmptyExpression' && ehExpressaoDeTexto(so) ? so : null;

	return {
		loc: `${at(open, 'start')}-${at(open, 'end')}`,
		name: jsxName(open.name as Node),
		start: at(open, 'start'),
		end: at(open, 'end'),
		nameEnd: at(open.name as Node, 'end'),
		outerEnd: at(el, 'end'),
		attrs: ((open.attributes ?? []) as Node[]).filter((a) => a.type === 'JSXAttribute').map(toAttr),
		text: text ? { start: at(text, 'start'), end: at(text, 'end') } : null,
		inner: close ? { start: at(open, 'end'), end: at(close, 'start') } : null,
		expr: expr ? { start: at(expr, 'start'), end: at(expr, 'end') } : null,
		folhaDeTexto: uteis.length > 0 && (uteis.every((c) => c.type === 'JSXText') || !!expr),
	};
}

function elementsDo(ast: unknown): JsxElement[] {
	const out: JsxElement[] = [];
	walk(ast, (n) => {
		if (n.type === 'JSXElement') out.push(describe(n));
	});
	return out.sort((a, b) => a.start - b.start);
}

/** Todos os elementos JSX, em ordem de posição no arquivo. */
export function elements(src: string): JsxElement[] {
	return elementsDo(parseJsx(src));
}

export function alvo(sel: string): { loc: string; index: number | null } {
	const corte = sel.indexOf('#');
	if (corte < 0) return { loc: sel, index: null };
	const n = Number(sel.slice(corte + 1));
	return { loc: sel.slice(0, corte), index: Number.isInteger(n) ? n : null };
}

export function elementAt(src: string, sel: string): JsxElement | null {
	const { loc } = alvo(sel);
	return elements(src).find((e) => e.loc === loc) ?? null;
}

export function raizDoBloco(src: string): JsxElement | null {
	try {
		return raizEntre(elements(src));
	} catch {
		return null; // código que não parseia é problema de outro lugar
	}
}

function raizEntre(todos: JsxElement[]): JsxElement | null {
	const topo = todos.filter((e) => !todos.some((o) => o !== e && o.start < e.start && e.outerEnd <= o.outerEnd));
	return topo[topo.length - 1] ?? null;
}

/** Contenção por offset: o AST não tem ponteiro para o pai e não precisa ter. */
function dentroDe(fora: JsxElement, dentro: JsxElement): boolean {
	return fora !== dentro && fora.start < dentro.start && dentro.outerEnd <= fora.outerEnd;
}

/** Tag que só carrega texto — o modelo escreve preço em <div> tanto quanto em <span>. */
const TEXTUAL = /^(h[1-6]|p|span|strong|em|b|i|small|label|li|div|figcaption|blockquote|dt|dd|td|th)$/;

/** Classe que desenha alguma coisa. `flex`, `gap-4` e `items-center` não desenham. */
const PINTURA = /^(bg-\S+|border(-\S+)?|shadow(-\S+)?|ring(-\S+)?)$/;

function pinta(el: JsxElement, src: string): boolean {
	return src
		.slice(el.start, el.end)
		.split(/[\s"'`{}=]+/)
		.some((t) => PINTURA.test(t));
}

export function papeis(src: string): Map<string, Papel> {
	try {
		return papeisEntre(elements(src), src);
	} catch {
		return new Map();
	}
}

function papeisEntre(todos: JsxElement[], src: string): Map<string, Papel> {
	const raiz = raizEntre(todos);
	const botoes = todos.filter((e) => e.name === 'button' || e.name === 'a');
	const out = new Map<string, Papel>();

	for (const el of todos) {
		// dentro de botão o alvo é o botão inteiro, texto ou superfície
		const noBotao = botoes.some((b) => dentroDe(b, el));

		if (el === raiz) out.set(el.loc, 'raiz');
		else if (el.name === 'img') out.set(el.loc, 'imagem');
		else if (el.name === 'button' || el.name === 'a') out.set(el.loc, 'botao');
		else if (noBotao) continue;
		else if (TEXTUAL.test(el.name) && el.folhaDeTexto) out.set(el.loc, 'texto');
		else if (pinta(el, src)) out.set(el.loc, 'superficie');
	}
	return out;
}

export function papelDe(src: string, sel: string): Papel | null {
	return papeis(src).get(alvo(sel).loc) ?? null;
}

export function selDoTexto(src: string, sel: string): string | null {
	const { loc, index } = alvo(sel);
	let todos: JsxElement[];
	try {
		todos = elements(src);
	} catch {
		return null;
	}

	const el = todos.find((e) => e.loc === loc);
	if (!el) return null;

	const dono = el.folhaDeTexto ? el : todos.find((o) => dentroDe(el, o) && o.folhaDeTexto);
	if (!dono) return null;
	return index === null ? dono.loc : `${dono.loc}#${index}`;
}

export function classAttr(el: JsxElement): JsxAttr | null {
	return el.attrs.find((a) => a.name === 'className') ?? el.attrs.find((a) => a.name === 'class') ?? null;
}

interface Mapa {
	/** corpo do callback: quem estiver aqui dentro é instância */
	start: number;
	end: number;
	/** o parâmetro do item — `s` em SLIDES.map((s) => …) */
	item: string;
	/** o parâmetro do índice, escrito pelo modelo ou criado por nós */
	indice: string;
	/** a emenda que cria o parâmetro, quando ele faltava */
	emenda: Emenda | null;
	/** os itens do array percorrido, quando ele é literal */
	itens: Node[] | null;
}

interface Emenda {
	start: number;
	end: number;
	texto: string;
}

/** Quantas vezes um nome aparece na árvore — para saber se um setter é usado. */
function citacoes(ast: unknown, nome: string): number {
	let n = 0;
	walk(ast, (no) => {
		if (no.type === 'Identifier' && no.name === nome) n++;
	});
	return n;
}

/** O array literal por trás de um inicializador, atravessando o que preserva a ordem. */
function arrayDe(init: Node | null): Node[] | null {
	if (!init) return null;
	if (init.type === 'ArrayExpression') return (init.elements ?? []) as Node[];
	if (init.type !== 'CallExpression') return null;

	const chamada = String((init.callee as Node | null)?.name ?? '');
	const args = (init.arguments ?? []) as Node[];

	if (chamada === 'useState') return arrayDe(args[0] ?? null);
	if (chamada === 'useMemo') {
		const fn = args[0];
		return fn?.type === 'ArrowFunctionExpression' ? arrayDe(fn.body as Node) : null;
	}
	return null;
}

function arraysLiterais(ast: unknown): Map<string, Node[]> {
	const out = new Map<string, Node[]>();

	walk(ast, (n) => {
		if (n.type !== 'VariableDeclarator') return;

		const itens = arrayDe(n.init as Node | null);
		if (!itens) return;

		const id = n.id as Node | null;
		if (id?.type === 'Identifier') {
			out.set(String(id.name), itens);
			return;
		}
		if (id?.type !== 'ArrayPattern') return;

		// const [slides, setSlides] = useState([...])
		const partes = (id.elements ?? []) as Node[];
		const valor = partes[0];
		const setter = partes[1];
		if (valor?.type !== 'Identifier') return;

		// o setter só aparece na própria declaração = a lista nunca é reescrita
		if (setter?.type === 'Identifier' && citacoes(ast, String(setter.name)) > 1) return;

		out.set(String(valor.name), itens);
	});

	return out;
}

function mapasDe(ast: unknown, src: string): Mapa[] {
	// escopo achatado: bloco é um arquivo só, e o array vive no topo dele
	const arrays = arraysLiterais(ast);

	const out: Mapa[] = [];
	walk(ast, (n) => {
		if (n.type !== 'CallExpression') return;
		const callee = n.callee as Node | null;
		if (callee?.type !== 'MemberExpression' || callee.computed) return;
		if (String((callee.property as Node)?.name) !== 'map') return;

		const fn = ((n.arguments ?? []) as Node[])[0];
		if (!fn || (fn.type !== 'ArrowFunctionExpression' && fn.type !== 'FunctionExpression')) return;

		const params = (fn.params ?? []) as Node[];
		const primeiro = params[0];
		if (!primeiro) return;
		if (params.length > 1 && params[1].type !== 'Identifier') return;

		const alvoArr = callee.object as Node;
		const itens =
			alvoArr.type === 'ArrayExpression'
				? ((alvoArr.elements ?? []) as Node[])
				: alvoArr.type === 'Identifier'
					? (arrays.get(String(alvoArr.name)) ?? null)
					: null;

		const escrito = params[1]?.type === 'Identifier' ? String(params[1].name) : null;
		const criado = `__i${out.length}`;
		const item = primeiro.type === 'Identifier' ? String(primeiro.name) : '';

		let abre = at(fn, 'start');
		if (src.startsWith('async', abre)) abre += 5;
		while (abre < src.length && /\s/.test(src[abre])) abre++;
		const semParenteses = src[abre] !== '(';

		out.push({
			start: at(fn.body as Node, 'start'),
			end: at(fn.body as Node, 'end'),
			item,
			indice: escrito ?? criado,
			emenda: escrito
				? null
				: semParenteses
					? { start: at(primeiro, 'start'), end: at(primeiro, 'end'), texto: `(${item}, ${criado})` }
					: { start: at(primeiro, 'end'), end: at(primeiro, 'end'), texto: `, ${criado}` },
			itens,
		});
	});
	return out;
}

export function repeticoesOpacas(src: string): string[] {
	let ast: unknown;
	try {
		ast = parseJsx(src);
	} catch {
		return [];
	}

	// a MESMA resolução que o editor usa: se ele alcança, não há o que avisar
	const literais = arraysLiterais(ast);

	const out: string[] = [];
	walk(ast, (n) => {
		if (n.type !== 'CallExpression') return;
		const callee = n.callee as Node | null;
		if (callee?.type !== 'MemberExpression' || callee.computed) return;
		if (String((callee.property as Node)?.name) !== 'map') return;

		// map sem JSX dentro é cálculo, não repetição de conteúdo
		const fn = ((n.arguments ?? []) as Node[])[0];
		if (!fn || ehExpressaoDeTexto(fn)) return;

		const item = ((fn.params ?? []) as Node[])[0];
		if (!item) return;
		if (item.type === 'Identifier' && !usaParametro(fn.body, String(item.name))) return;

		const obj = callee.object as Node;
		const ok = obj.type === 'ArrayExpression' || (obj.type === 'Identifier' && literais.has(String(obj.name)));
		if (ok && item.type === 'Identifier') return;

		out.push(src.slice(at(obj, 'start'), at(obj, 'end')).slice(0, 40));
	});
	return out;
}

function usaParametro(corpo: unknown, nome: string): boolean {
	let usa = false;

	const visita = (no: unknown): void => {
		if (usa || !no || typeof no !== 'object') return;
		if (Array.isArray(no)) {
			no.forEach(visita);
			return;
		}

		const n = no as Node;
		if (n.type === 'Identifier' && n.name === nome) {
			usa = true;
			return;
		}

		for (const chave in n) {
			if (chave === 'loc' || chave === 'leadingComments' || chave === 'trailingComments') continue;
			// citar o nome não é usá-lo: `x.item` e `{ item: 1 }` só o soletram
			const soletra =
				!n.computed &&
				((n.type === 'MemberExpression' && chave === 'property') || (n.type === 'ObjectProperty' && chave === 'key'));
			if (soletra) continue;

			visita(n[chave]);
		}
	};

	visita(corpo);
	return usa;
}

export type TipoCampo = 'texto' | 'numero' | 'cor' | 'foto' | 'booleano' | 'vazio';

export interface CampoDado {
	chave: string;
	tipo: TipoCampo;
	/** o valor como está no código, sem aspas */
	valor: string;
	/** miolo do literal — é onde o splice escreve */
	start: number;
	end: number;
}

export interface ItemDado {
	/** o primeiro texto do item, para dar nome ao grupo na tela */
	rotulo: string;
	campos: CampoDado[];
}

export interface ListaDados {
	nome: string;
	itens: ItemDado[];
}

/** O tipo sai do próprio literal: hex é cor, url é foto, o resto é o que parece. */
function campoDoLiteral(chave: string, v: Node): CampoDado | null {
	const faixa = { start: at(v, 'start'), end: at(v, 'end') };

	if (v.type === 'StringLiteral') {
		const valor = String(v.value);
		const tipo: TipoCampo = /^#[0-9a-fA-F]{3,8}$/.test(valor)
			? 'cor'
			: /^(https?:\/\/|data:image\/)/.test(valor)
				? 'foto'
				: 'texto';
		// +1/-1: as aspas ficam de fora, como no resto do arquivo
		return { chave, tipo, valor, start: faixa.start + 1, end: faixa.end - 1 };
	}
	if (v.type === 'NumericLiteral') return { chave, tipo: 'numero', valor: String(v.value), ...faixa };
	if (v.type === 'BooleanLiteral') return { chave, tipo: 'booleano', valor: String(v.value), ...faixa };
	if (v.type === 'NullLiteral') return { chave, tipo: 'vazio', valor: '', ...faixa };

	// template, chamada, objeto aninhado: não há literal simples para trocar
	return null;
}

export function listasDoBloco(src: string): ListaDados[] {
	let ast: unknown;
	try {
		ast = parseJsx(src);
	} catch {
		return [];
	}

	const arrays = arraysLiterais(ast);

	// só lista o que de fato vira tela: array de configuração não é conteúdo
	const renderizados = new Set<string>();
	walk(ast, (n) => {
		if (n.type !== 'CallExpression') return;
		const callee = n.callee as Node | null;
		if (callee?.type !== 'MemberExpression' || callee.computed) return;
		if (String((callee.property as Node)?.name) !== 'map') return;

		const fn = ((n.arguments ?? []) as Node[])[0];
		if (!fn || ehExpressaoDeTexto(fn)) return; // sem JSX dentro é cálculo

		const obj = callee.object as Node;
		if (obj.type === 'Identifier' && arrays.has(String(obj.name))) renderizados.add(String(obj.name));
	});

	const out: ListaDados[] = [];
	for (const nome of renderizados) {
		const itens: ItemDado[] = [];

		for (const el of arrays.get(nome) ?? []) {
			if (el?.type !== 'ObjectExpression') continue;

			const campos: CampoDado[] = [];
			for (const p of (el.properties ?? []) as Node[]) {
				if (p.type !== 'ObjectProperty' || p.computed) continue;
				const chave = nomeDaChave(p.key as Node);
				const campo = chave ? campoDoLiteral(chave, p.value as Node) : null;
				if (campo) campos.push(campo);
			}
			if (!campos.length) continue;

			itens.push({ rotulo: campos.find((c) => c.tipo === 'texto')?.valor || `Item ${itens.length + 1}`, campos });
		}

		if (itens.length) out.push({ nome, itens });
	}

	return out.sort((a, b) => a.nome.localeCompare(b.nome));
}

/** O map mais interno que contém o offset — map dentro de map resolve pelo de dentro. */
function mapaDe(mapas: Mapa[], start: number): Mapa | null {
	let melhor: Mapa | null = null;
	for (const m of mapas) {
		if (start < m.start || start >= m.end) continue;
		if (!melhor || m.start > melhor.start) melhor = m;
	}
	return melhor;
}

export function instrument(src: string): string {
	const ast = parseJsx(src);
	const todos = elementsDo(ast);
	const papel = papeisEntre(todos, src);
	const mapas = mapasDe(ast, src);

	const emendas: Emenda[] = [];
	const usados = new Set<Mapa>();

	for (const el of todos) {
		const m = mapaDe(mapas, el.start);
		if (m) usados.add(m);
		const loc = m ? `{${JSON.stringify(`${el.loc}#`)} + ${m.indice}}` : `"${el.loc}"`;
		const p = papel.get(el.loc);
		emendas.push({ start: el.nameEnd, end: el.nameEnd, texto: ` data-cb-loc=${loc}${p ? ` data-cb-role="${p}"` : ''}` });
	}

	// só os maps que renderizam algo selecionável ganham o parâmetro
	for (const m of usados) if (m.emenda) emendas.push(m.emenda);

	// de trás para frente: assim os offsets ainda não usados continuam válidos
	emendas.sort((a, b) => b.start - a.start);
	let out = src;
	for (const e of emendas) out = out.slice(0, e.start) + e.texto + out.slice(e.end);
	return out;
}

export interface Origem {
	/** miolo do literal, sem aspas */
	start: number;
	end: number;
	valor: string;
	/** literal cru dentro de um elemento repetido: editar aqui muda todas as cópias */
	compartilhado: boolean;
	/** true no JSX (escapa com &quot;), false no array (escapa com barra) */
	jsx: boolean;
}

export function origemDoValor(src: string, sel: string, campo: string | null): Origem | null {
	const { loc, index } = alvo(sel);

	let ast: unknown;
	try {
		ast = parseJsx(src);
	} catch {
		return null;
	}

	const achados: Node[] = [];
	walk(ast, (n) => {
		if (n.type !== 'JSXElement') return;
		const open = n.openingElement as Node;
		if (`${at(open, 'start')}-${at(open, 'end')}` === loc) achados.push(n);
	});
	const el = achados[0];
	if (!el) return null;

	const mapa = mapaDe(mapasDe(ast, src), at(el.openingElement as Node, 'start'));
	const no = campo === null ? conteudoDe(el) : atributoDe(el, campo);
	if (!no) return null;

	if (no.type === 'JSXText') {
		const bruto = String(no.value);
		const inicio = at(no, 'start') + (/^\s*/.exec(bruto)?.[0].length ?? 0);
		return { start: inicio, end: inicio + bruto.trim().length, valor: bruto.trim(), compartilhado: !!mapa, jsx: true };
	}
	if (no.type === 'StringLiteral') {
		return { start: at(no, 'start') + 1, end: at(no, 'end') - 1, valor: String(no.value), compartilhado: !!mapa, jsx: true };
	}

	// expressão: o literal está no array, na posição desta instância
	if (!mapa?.itens || index === null) return null;
	const caminho = caminhoDe(no, mapa.item);
	if (!caminho) return null;

	let alvoNo: Node | null = mapa.itens[index] ?? null;
	for (const chave of caminho) alvoNo = propriedade(alvoNo, chave);
	if (alvoNo?.type !== 'StringLiteral') return null;

	return { start: at(alvoNo, 'start') + 1, end: at(alvoNo, 'end') - 1, valor: String(alvoNo.value), compartilhado: false, jsx: false };
}

function atributoDe(el: Node, nome: string): Node | null {
	const attrs = ((el.openingElement as Node).attributes ?? []) as Node[];
	const a = attrs.find((x) => x.type === 'JSXAttribute' && jsxName(x.name as Node) === nome);
	const v = (a?.value ?? null) as Node | null;
	if (!v) return null;
	return v.type === 'JSXExpressionContainer' ? (v.expression as Node) : v;
}

function conteudoDe(el: Node): Node | null {
	const uteis = filhosUteis((el.children ?? []) as Node[]);
	if (uteis.length === 1 && uteis[0].type === 'JSXExpressionContainer') return uteis[0].expression as Node;
	return uteis.find((c) => c.type === 'JSXText') ?? null;
}

/** `s` → [], `s.foto` → ['foto']. Índice computado ou outra raiz devolve null. */
function caminhoDe(no: Node, item: string): string[] | null {
	const out: string[] = [];
	let atual = no;
	while (atual.type === 'MemberExpression') {
		const prop = atual.property as Node;
		if (atual.computed || prop?.type !== 'Identifier') return null;
		out.unshift(String(prop.name));
		atual = atual.object as Node;
	}
	return atual.type === 'Identifier' && String(atual.name) === item ? out : null;
}

function propriedade(no: Node | null, chave: string): Node | null {
	if (no?.type !== 'ObjectExpression') return null;
	const p = ((no.properties ?? []) as Node[]).find(
		(x) => x.type === 'ObjectProperty' && !x.computed && nomeDaChave(x.key as Node) === chave,
	);
	return (p?.value ?? null) as Node | null;
}

function nomeDaChave(k: Node): string {
	if (k.type === 'Identifier') return String(k.name);
	if (k.type === 'StringLiteral') return String(k.value);
	return '';
}
